'use client';

import { useEffect, useState } from 'react';

import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import { validateInvitationToken } from 'src/auth/context/jwt';

// ----------------------------------------------------------------------

const ACTIVE_REDIRECT_SECONDS = 3;

export type InvitationTokenCheckStatus =
  | 'idle'
  | 'checking'
  | 'valid'
  | 'invalid'
  | 'activeRedirect';

export type InvitationTokenValidationResult = {
  status: InvitationTokenCheckStatus;
  /** Seconds remaining before redirect when `status === 'activeRedirect'` (counts down to 0). */
  activeRedirectCountdown: number | null;
};

/** Pass `true` for flows where active users may continue (e.g. reset password from email). */
export function useInvitationTokenValidation(
  urlToken: string | null,
  allowActiveUser = false
): InvitationTokenValidationResult {
  const router = useRouter();

  const [status, setStatus] = useState<InvitationTokenCheckStatus>(() =>
    urlToken ? 'checking' : 'idle'
  );

  const [activeRedirectCountdown, setActiveRedirectCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!urlToken) {
      setStatus('idle');
      setActiveRedirectCountdown(null);
      return undefined;
    }

    setStatus('checking');
    setActiveRedirectCountdown(null);

    let cancelled = false;

    (async () => {
      try {
        const result = await validateInvitationToken(urlToken);
        if (cancelled) {
          return;
        }
        if (result.active) {
          if (allowActiveUser) {
            setStatus(result.valid ? 'valid' : 'invalid');
          } else {
            setStatus('activeRedirect');
            setActiveRedirectCountdown(ACTIVE_REDIRECT_SECONDS);
          }
          return;
        }
        setStatus(result.valid ? 'valid' : 'invalid');
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          setStatus('idle');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [urlToken, allowActiveUser]);

  useEffect(() => {
    if (status !== 'activeRedirect' || activeRedirectCountdown === null) {
      return undefined;
    }

    if (activeRedirectCountdown === 0) {
      router.replace(paths.auth.jwt.signIn);
      return undefined;
    }

    const id = window.setTimeout(() => {
      setActiveRedirectCountdown((c) => (c === null || c <= 0 ? c : c - 1));
    }, 1000);

    return () => {
      window.clearTimeout(id);
    };
  }, [status, activeRedirectCountdown, router]);

  return { status, activeRedirectCountdown };
}
